import React, { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { WithholdingService } from '../../../services/withholding.service';
import { AccountService } from '../../../services/account.service';
import { AuthService } from '../../../services/auth.service';
import {
  PaymentWithholding, WithholdingDeposit, WithholdingType,
} from '../../../interfaces/procurement/withholding.interface';
import { Account } from '../../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../../common/AccountAutocomplete';
import { depositAccountOptions } from '../../../utils/postingAccounts';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import { formatMoney, humanizeEnum } from '../../../utils/format';
import { formatDate, firstOfMonthLocalIso, toLocalIsoDate } from '../../../utils/dateUtils';

export const WithholdingDeposits: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const canManage = usePermission(PERMISSIONS.SUPPLIER_PAYMENT_MANAGE);
  const [type, setType] = useState<WithholdingType>('VDS');
  const [from, setFrom] = useState(firstOfMonthLocalIso());
  const [to, setTo] = useState(toLocalIsoDate());
  const [rows, setRows] = useState<PaymentWithholding[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deposits, setDeposits] = useState<WithholdingDeposit[]>([]);
  const [banks, setBanks] = useState<Account[]>([]);
  const [challan, setChallan] = useState('');
  const [depositDate, setDepositDate] = useState(toLocalIsoDate());
  const [bankAccountId, setBankAccountId] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadDeposits = async () => {
    try { setDeposits((await WithholdingService.listDeposits(institutionId)) ?? []); }
    catch (e) { toast.showApiError(e, 'Failed to load deposits'); }
  };

  const loadRows = async () => {
    try {
      setLoading(true);
      setSelected(new Set());
      setRows((await WithholdingService.listUndeposited(institutionId, type, from, to)) ?? []);
    } catch (e) {
      toast.showApiError(e, 'Failed to load undeposited withholdings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeposits();
    AccountService.getAccountsByType('ASSET')
      .then((a: Account[]) => setBanks(depositAccountOptions(a ?? [])))
      .catch((e: unknown) => toast.showApiError(e, 'Failed to load accounts'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadRows(); }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const total = rows.filter(r => selected.has(r.id)).reduce((s, r) => s + r.amount, 0);

  const record = async () => {
    try {
      setSaving(true);
      await WithholdingService.createDeposit(institutionId, {
        type, challanNumber: challan, depositDate, bankAccountId,
        withholdingIds: Array.from(selected), notes: notes || undefined,
      });
      toast.showSuccess('Deposit recorded');
      setChallan(''); setNotes('');
      await Promise.all([loadRows(), loadDeposits()]);
    } catch (e) {
      toast.showApiError(e, 'Failed to record the deposit');
    } finally {
      setSaving(false);
    }
  };

  const reverse = async (d: WithholdingDeposit) => {
    try {
      await WithholdingService.reverseDeposit(d.id);
      toast.showSuccess('Deposit reversed');
      await Promise.all([loadRows(), loadDeposits()]);
    } catch (e) {
      toast.showApiError(e, 'Failed to reverse the deposit');
    }
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.whDeposit.page">
      <Typography variant="h5" sx={{ mb: 2 }}>Withholding deposits (challan)</Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
          <TextField data-help="procurement.whDepositForm.field.type" select size="small" label="Type" value={type}
            onChange={e => setType(e.target.value as WithholdingType)} sx={{ flex: '1 1 130px', maxWidth: 180 }}>
            <MenuItem value="VDS">VDS</MenuItem>
            <MenuItem value="TDS">TDS</MenuItem>
          </TextField>
          <TextField data-help="procurement.whDepositForm.field.from" label="Paid from" type="date" size="small" value={from} onChange={e => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 200 }} />
          <TextField data-help="procurement.whDepositForm.field.to" label="Paid to" type="date" size="small" value={to} onChange={e => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 200 }} />
          <Button data-help="procurement.whDeposit.show" variant="outlined" onClick={loadRows}>Show undeposited</Button>
        </Box>
        {loading ? <CircularProgress /> : (
          <Box sx={{ overflowX: 'auto' }} data-help-section="procurement.whDeposit.pick">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" data-help="procurement.whDeposit.pick.select">
                    <Checkbox data-help="procurement.whDepositForm.field.selectAll" checked={rows.length > 0 && selected.size === rows.length}
                      indeterminate={selected.size > 0 && selected.size < rows.length}
                      onChange={e => setSelected(e.target.checked ? new Set(rows.map(r => r.id)) : new Set())}
                      inputProps={{ 'aria-label': 'Select all' }} />
                  </TableCell>
                  <TableCell data-help="procurement.whDeposit.pick.date">Date</TableCell><TableCell data-help="procurement.whDeposit.pick.cert">Certificate</TableCell><TableCell data-help="procurement.whDeposit.pick.supplier">Supplier</TableCell>
                  <TableCell data-help="procurement.whDeposit.pick.bill">Bill</TableCell><TableCell data-help="procurement.whDeposit.pick.amount" align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={6}>Nothing undeposited for this selection.</TableCell></TableRow>}
                {rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox data-help="procurement.whDeposit.action.select" checked={selected.has(r.id)} onChange={() => toggle(r.id)}
                        inputProps={{ 'aria-label': `Select ${r.certificateNumber}` }} />
                    </TableCell>
                    <TableCell>{formatDate(r.paymentDate)}</TableCell>
                    <TableCell>{r.certificateNumber}</TableCell>
                    <TableCell>{r.supplierName}</TableCell>
                    <TableCell>{r.billInvoiceRef || r.billNumber}</TableCell>
                    <TableCell align="right">{formatMoney(r.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {canManage && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 2 }} data-help-section="procurement.whDepositForm.section.record">
            <TextField data-help="procurement.whDepositForm.field.challan" label="Challan no" size="small" value={challan} onChange={e => setChallan(e.target.value)}
              sx={{ flex: '1 1 180px', maxWidth: 240 }} />
            <TextField data-help="procurement.whDepositForm.field.depositDate" label="Deposit date" type="date" size="small" value={depositDate}
              onChange={e => setDepositDate(e.target.value)} InputLabelProps={{ shrink: true }}
              sx={{ flex: '1 1 150px', maxWidth: 200 }} />
            <Box sx={{ flex: '2 1 260px', maxWidth: 380 }} data-help="procurement.whDepositForm.field.account">
              <AccountAutocomplete accounts={banks} value={bankAccountId} onChange={setBankAccountId}
                label="Bank / cash account" size="small" />
            </Box>
            <TextField data-help="procurement.whDepositForm.field.notes" label="Notes" size="small" value={notes} onChange={e => setNotes(e.target.value)}
              sx={{ flex: '2 1 200px', maxWidth: 320 }} />
            <Typography variant="body2">Selected: <b>{formatMoney(total)}</b></Typography>
            <Button data-help="procurement.whDeposit.record" variant="contained" onClick={record}
              disabled={saving || selected.size === 0 || !challan.trim() || !bankAccountId}>
              Record deposit
            </Button>
          </Box>
        )}
      </Paper>

      <Typography variant="h6" sx={{ mb: 1 }}>Deposits</Typography>
      <Paper sx={{ overflowX: 'auto' }} data-help-section="procurement.whDeposit.table">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell data-help="procurement.whDeposit.col.date">Date</TableCell><TableCell data-help="procurement.whDeposit.col.type">Type</TableCell><TableCell data-help="procurement.whDeposit.col.challan">Challan</TableCell>
              <TableCell data-help="procurement.whDeposit.col.amount" align="right">Amount</TableCell><TableCell data-help="procurement.whDeposit.col.certs">Certificates</TableCell>
              <TableCell data-help="procurement.whDeposit.col.status">Status</TableCell><TableCell data-help="procurement.whDeposit.col.actions" />
            </TableRow>
          </TableHead>
          <TableBody>
            {deposits.length === 0 && <TableRow><TableCell colSpan={7}>No deposits yet.</TableCell></TableRow>}
            {deposits.map(d => (
              <TableRow key={d.id}>
                <TableCell>{formatDate(d.depositDate)}</TableCell>
                <TableCell>{d.type}</TableCell>
                <TableCell>{d.challanNumber}</TableCell>
                <TableCell align="right">{formatMoney(d.totalAmount)}</TableCell>
                <TableCell>{d.certificateCount}</TableCell>
                <TableCell>{humanizeEnum(d.status)}</TableCell>
                <TableCell>
                  {canManage && d.status === 'POSTED' && (
                    <Button data-help="procurement.whDeposit.action.reverse" size="small" color="error" onClick={() => reverse(d)}>Reverse</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default WithholdingDeposits;
